import { useCallback, useEffect, useState } from "react"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"

export interface PublicSchool {
  id: string
  code: string
  name: string
  city: string
  state: string
  enrollment: number
  type: "primary" | "secondary" | "combined"
  since: number | null
}

export interface PublicProgram {
  id: string
  code: string
  name: string
  description: string | null
  icon: "book" | "scissor" | "atom"
}

export interface PublicEvent {
  id: string
  code: string
  programId: string
  programName: string | null
  title: string
  type: "workshop" | "showcase" | "graduation" | "field_trip" | "other"
  date: string
  startTime: string
  endTime: string | null
  venue: string | null
  bannerUrl: string | null
  description: string | null
}

async function readData<T>(response: { ok: boolean; json: () => Promise<unknown> }): Promise<T> {
  const body = (await response.json()) as { data?: T; message?: string }
  if (!response.ok || body.data === undefined) throw new Error(body.message ?? "Could not load public content")
  return body.data
}

export async function fetchPublicSchools(): Promise<PublicSchool[]> {
  return readData(await client.api.public.schools.$get())
}

export async function fetchPublicPrograms(): Promise<PublicProgram[]> {
  return readData(await client.api.public.programs.$get())
}

export async function fetchPublicEvents(): Promise<PublicEvent[]> {
  return readData(await client.api.public.events.$get())
}

export async function fetchPublicEvent(id: string): Promise<PublicEvent> {
  return readData(await client.api.public.events[":id"].$get({ param: { id } }))
}

function usePublicResource<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void load()
      .then((nextData) => {
        if (!cancelled) setData(nextData)
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "Could not load public content")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [load])

  return { data, loading, error }
}

export function usePublicSchools() {
  return usePublicResource(fetchPublicSchools)
}

export function usePublicPrograms() {
  return usePublicResource(fetchPublicPrograms)
}

export function usePublicEvents() {
  return usePublicResource(fetchPublicEvents)
}

export function usePublicEvent(id: string) {
  const loader = useCallback(() => fetchPublicEvent(id), [id])
  return usePublicResource(loader)
}

export { getErrorMessage }
